import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface SupabaseProject {
  id: string;
  name: string;
  organization_id: string;
  region: string;
  created_at: string;
}

class SupabaseManager {
  private client: SupabaseClient | null = null;
  private initialized: boolean = false;
  private supabaseUrl: string = '';
  private supabaseAnonKey: string = '';

  constructor() {
    const handler = (window as any).electron;
    this.supabaseUrl = handler?.env?.SUPABASE_URL || process.env.SUPABASE_URL || '';
    this.supabaseAnonKey = handler?.env?.SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY || '';
  }

  get isConfigured(): boolean {
    return !!(this.supabaseUrl && this.supabaseAnonKey);
  }

  get isInitialized(): boolean {
    return this.initialized && this.client !== null;
  }

  getProjects(): SupabaseProject[] {
    return []; // Deprecated: Project switching via PAT is removed for security
  }

  getCurrentProjectRef(): string | null {
    return null;
  }

  getClient(): SupabaseClient | null {
    return this.client;
  }

  async initialize(forceProjectRef?: string): Promise<boolean> {
    if (!this.isConfigured) {
      this.initialized = false;
      return false;
    }

    try {
      if (this.supabaseUrl && this.supabaseAnonKey) {
        this.client = createClient(this.supabaseUrl, this.supabaseAnonKey, {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: false,
          },
        });
        this.initialized = true;
        return true;
      }
      return false;
    } catch (err) {
      console.error('Supabase initialization failed:', err);
      this.client = null;
      this.initialized = false;
      return false;
    }
  }

  // Auth helper methods
  async signUp(email: string, password: string, username: string) {
    if (!this.client) throw new Error('Supabase client is not initialized');
    const { data, error } = await this.client.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
        },
      },
    });
    return { data, error };
  }

  async signIn(email: string, password: string) {
    if (!this.client) throw new Error('Supabase client is not initialized');
    const { data, error } = await this.client.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  }

  async signOut() {
    if (!this.client) return;
    await this.client.auth.signOut();
  }

  async getProfile(userId: string) {
    if (!this.client) throw new Error('Supabase client is not initialized');
    const { data, error } = await this.client
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  }

  async updateProfile(username: string, avatarUrl: string) {
    if (!this.client) throw new Error('Supabase client is not initialized');
    const { data, error } = await this.client.auth.updateUser({
      data: {
        username,
        avatarUrl,
      },
    });

    if (error) return { data, error };

    const user = data.user;
    if (user) {
      const { error: dbError } = await this.client
        .from('profiles')
        .upsert({
          id: user.id,
          username,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        });
      if (dbError) {
        console.error('Failed to upsert profile to DB:', dbError);
        return { data, error: dbError };
      }
    }

    return { data, error: null };
  }

  async updatePassword(password: string) {
    if (!this.client) throw new Error('Supabase client is not initialized');
    const { data, error } = await this.client.auth.updateUser({
      password,
    });
    return { data, error };
  }
}

export const supabaseManager = new SupabaseManager();
